import React, { Component } from 'react'
import Swal from "sweetalert2"
import { BsJournals } from 'react-icons/bs'
import { AiOutlineSearch, AiOutlineWarning } from 'react-icons/ai'
import { Modal } from 'react-bootstrap'
import ModalOrders from '../../components/ModalComponent'
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import { getIndexElement } from '../../functions/searchInObject'
import { NumberFormat } from '../../functions/generalFunctions'
import { pricesFormat } from '../../functions/pricesFormat.js'
import { buildCartLine } from './buildCartLine'
import '../../css/general-style.css'
import '../../css/so-v3-catalogue-responsive.css' // RESP-2026-11-02
// OFFLINE-2026-09-28, Phase 1 — additive fallback only, see this file's own
// updated header comment below and offlineCatalogCache.js's header for the
// full design. The ONLINE fetch path (fetchCatalog/fetchProductLines) is
// untouched; these are only called from the NEW catch/offline branches.
import {
    prefetchOfflineCatalog, prefetchOfflineCustomers, getCachedCustomers,
    searchOfflineCatalog, getOfflineProductLines, isOnline,
} from '../../services/offlineCatalogCache'

/**
 * CatalogueV3 — product browser for Sales Order V3. Rebuilt from scratch
 * (2026-08), replacing CatalogueV2.js (1100+ lines: offline caching, service
 * worker thumbnail cache, per-item class-like objects with attached methods,
 * plus its OWN Altitude markup calculation duplicating what SalesOrderV2.js
 * and the backend's addInformation() each computed differently — 3
 * inconsistent formulas for the same number, confirmed 2026-08-13).
 *
 * This version: RDS-only price from product_pricing (/v3/salesorder/catalog —
 * no MAS90/WS call anywhere), simple paginated grid, product-line filter,
 * search. Deliberately dropped the offline/service-worker caching layer at
 * the time — flagged to the user as a scope trade-off.
 *
 * OFFLINE-2026-09-28 (Phase 1) — that trade-off started hurting real
 * fieldwork (salespeople visiting customers with poor/no signal couldn't see
 * the catalog or prices at all, ended up quoting made-up numbers) — see
 * memory project_so_v3_offline_catalog_pending.md. Phase 1 restores
 * view/search/pricing offline (NOT adding to cart yet, that's Phase 2) via a
 * brand-new, isolated cache module (offlineCatalogCache.js) — deliberately
 * NOT a port of CatalogueV2.js's old cache (built before the 4-tier pricing
 * system existed) and deliberately NOT touching the online fetch functions
 * below, only adding a fallback branch to each. To revert Phase 1: remove
 * the `OFFLINE-2026-09-28` blocks in this file (search that tag) and delete
 * offlineCatalogCache.js + its 2 backend routes.
 *
 * Follows the "Product list card" pattern already documented in this
 * project's CLAUDE.md design system.
 *
 * Tiered customer pricing (2026-08-19): passes the SO's selected customer to
 * /v3/salesorder/catalog, so `row.standardPrice` is already that customer's
 * tier price (level1/2/3, default level 1) — nothing else in this file needs
 * to know about tiers. The toolbar badge just reflects what the backend
 * resolved, for visibility. See customerPriceLevel.ts / salesOrderCatalog.ts.
 */
export default class CatalogueV3 extends Component {
    state = {
        showModal: false,
        loading: false,
        products: [],
        total: 0,
        page: 1,
        pageSize: 30,
        search: '',
        productLines: [],
        selectedLine: '',
        // Resolved price tier for the SO's currently selected customer
        // (2026-08-19, tiered pricing) — comes back on every catalog row
        // (same value for all of them, since it's one customer), kept here
        // just so the toolbar badge doesn't need a separate request.
        priceLevel: 1,
        // OFFLINE-2026-09-28 — true only when the current product list came
        // from the local offline cache (no signal, or the live call failed),
        // so the toolbar can show a small "Offline" indicator instead of
        // silently showing possibly-stale data with no explanation.
        isOffline: false,
        // OFFLINE-2026-09-28 — true while the background prefetch (see open())
        // is actually in flight. 2026-09-29, client-reported: showing only a
        // "done" toast at the end wasn't enough — needs a visible "working"
        // state too, not just a flash at completion.
        syncingOffline: false,
        // IMGPREVIEW-2026-10-07 — product whose image is shown enlarged
        // ({ url, itemCode, description }), or null when the preview is closed.
        previewItem: null,
    }

    // Debounced auto-search (2026-09-01, client's ask — Andrea and other
    // tablet users used to get live results as they typed; now they have to
    // type then hit Search, which is slower on a tablet keyboard). Waits 1s
    // of no typing before searching automatically (dropped from 2s same day,
    // client's follow-up ask) — Enter and the Search button still search
    // immediately, clearing any pending timer so a manual search never
    // double-fires with the debounced one.
    searchDebounceTimer = null

    open = () => {
        this.setState({ showModal: true })
        this.fetchProductLines()
        this.fetchCatalog()
        // OFFLINE-2026-09-28 — fire-and-forget, non-blocking: keeps the
        // local copy warm whenever there's signal, so it's ready the next
        // time there isn't. No-ops internally if already fresh or offline.
        // 2026-09-29, client-reported — this ran with zero visible feedback,
        // so there was no way to tell it was actually doing anything. Now
        // shows a quick toast, but ONLY when it really fetched something
        // (both promises resolve to {synced:false} on every no-op case —
        // already fresh, offline, or the modal being reopened a minute
        // later — so this stays quiet almost all the time by design).
        const idCompany = getValueCookie('CompanyId')
        this.setState({ syncingOffline: true })
        Promise.all([
            prefetchOfflineCatalog(idCompany),
            prefetchOfflineCustomers(idCompany, getValueCookie('SalesPerson'), getValueCookie('TypeUser')),
        ]).then(([catalogResult, customersResult]) => {
            this.setState({ syncingOffline: false })
            if (catalogResult.synced || customersResult.synced) {
                Swal.fire({
                    toast: true, position: 'top-end', icon: 'success',
                    title: 'Catalog saved for offline use',
                    showConfirmButton: false, timer: 2200,
                })
            }
        })
    }

    close = () => {
        clearTimeout(this.searchDebounceTimer)
        this.setState({ showModal: false, previewItem: null })
    }

    openPreview = (row) => {
        this.setState({ previewItem: { url: row.itemURLImage, itemCode: row.itemCode, description: row.longDescription || row.description || '' } })
    }

    closePreview = () => this.setState({ previewItem: null })

    onSearchChange = (e) => {
        const value = e.target.value
        this.setState({ search: value })
        clearTimeout(this.searchDebounceTimer)
        this.searchDebounceTimer = setTimeout(() => {
            this.setState({ page: 1 }, () => this.fetchCatalog())
        }, 1000)
    }

    searchNow = () => {
        clearTimeout(this.searchDebounceTimer)
        this.setState({ page: 1 }, () => this.fetchCatalog())
    }

    async fetchProductLines() {
        if (this.state.productLines.length > 0) return
        const idCompany = getValueCookie('CompanyId')
        // OFFLINE-2026-09-28 — no signal at all: skip straight to the cached
        // copy instead of waiting out a network request that can only fail.
        if (!isOnline()) {
            this.setState({ productLines: getOfflineProductLines(idCompany) })
            return
        }
        try {
            // idCompany added (2026-09-01) — the endpoint now only returns
            // lines that actually have a priced/sellable item for THIS
            // company, instead of every line MAS90 has ever known about.
            const resp = await getInformationWithDataV3('/v3/salesorder/product-lines', { idCompany })
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ productLines: resp.data })
            } else {
                // OFFLINE-2026-09-28 — live call answered but wasn't usable
                // (e.g. a Sage hiccup mid-request); same offline fallback.
                this.setState({ productLines: getOfflineProductLines(idCompany) })
            }
        } catch (error) {
            console.error('fetchProductLines failed:', error)
            // OFFLINE-2026-09-28 — network error (signal dropped mid-request,
            // timeout, etc.) — fall back instead of leaving the filter empty.
            this.setState({ productLines: getOfflineProductLines(idCompany) })
        }
    }

    // OFFLINE-2026-09-28 — resolves the current customer's price tier from
    // the cached customer list, same default (Level 1) the backend itself
    // falls back to (COALESCE(cpl.priceLevel, 1) in salesOrderCatalog.ts).
    offlineCustomerPriceLevel(idCompany) {
        const customerNo = this.props.Padre.state.SalesOrder.Customer.CustomerNo
        const salespersonNo = getValueCookie('SalesPerson')
        const cached = getCachedCustomers(idCompany, salespersonNo)
        const match = cached.filter((c) => c.CustomerNo === customerNo)[0]
        return match && match.priceLevel ? Number(match.priceLevel) : 1
    }

    // OFFLINE-2026-09-28 — serves the catalog from the local cache: same
    // search/filter/pagination/price-resolution the online path would have
    // done server-side, done client-side instead. Sets isOffline:true so the
    // toolbar can tell the user what they're looking at.
    loadOfflineCatalog(idCompany) {
        const priceLevel = this.offlineCustomerPriceLevel(idCompany)
        const { data, total } = searchOfflineCatalog(idCompany, priceLevel, {
            search: this.state.search,
            productLine: this.state.selectedLine,
            page: this.state.page,
            pageSize: this.state.pageSize,
        })
        this.setState({ loading: false, products: data, total, priceLevel, isOffline: true })
    }

    async fetchCatalog() {
        this.setState({ loading: true })
        const idCompany = getValueCookie('CompanyId')
        // OFFLINE-2026-09-28 — no signal: don't even attempt the live call.
        if (!isOnline()) {
            this.loadOfflineCatalog(idCompany)
            return
        }
        try {
            const customerNo = this.props.Padre.state.SalesOrder.Customer.CustomerNo
            const resp = await getInformationWithDataV3('/v3/salesorder/catalog', {
                idCompany,
                customerNo,
                search: this.state.search || undefined,
                productLine: this.state.selectedLine || undefined,
                page: this.state.page,
                pageSize: this.state.pageSize,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                // OFFLINE-2026-09-28 — live call answered but failed (Sage
                // hiccup, etc.) — fall back instead of showing an empty grid,
                // same as the network-error branch below.
                this.loadOfflineCatalog(idCompany)
                return
            }
            const products = resp.data || []
            // Same resolved level on every row (one customer) — level 1 is the
            // safe default if the catalog came back empty.
            const priceLevel = products.length > 0 ? Number(products[0].resolvedPriceLevel) || 1 : 1
            // isOffline:false — explicit, in case a previous offline fallback
            // left it true and signal has since come back.
            this.setState({ loading: false, products, total: resp.total || 0, priceLevel, isOffline: false })
        } catch (error) {
            console.error('fetchCatalog failed:', error)
            // OFFLINE-2026-09-28 — network error mid-request (signal dropped
            // right as the request went out) — fall back to the cache.
            this.loadOfflineCatalog(idCompany)
        }
    }

    cartQuantity(itemCode) {
        const products = this.props.Padre.state.Products
        const index = getIndexElement(products, 'itemCode', itemCode)
        if (index === -1) return 0
        return products[index].Visible === 0 ? 0 : Number(products[index].quantityOrdered)
    }

    // itemCodes with a setQuantity currently in flight — Father.state.Products
    // is read synchronously here but setState() only commits async, so two
    // rapid clicks (or a click racing a scan from SearchUPCV3) on the same
    // item can both read the same stale "not in cart yet" snapshot and both
    // unshift a new line, producing a real duplicate row (client reported
    // exactly this class of bug on order AL64684 — see
    // salesOrderIntegrity.ts). This lock makes the second call for the same
    // itemCode a no-op until the first one's setState has committed.
    _inFlightItemCodes = new Set()

    setQuantity(row, quantity) {
        if (this._inFlightItemCodes.has(row.itemCode)) return
        this._inFlightItemCodes.add(row.itemCode)

        const Father = this.props.Padre
        const products = Father.state.Products
        const index = getIndexElement(products, 'itemCode', row.itemCode)
        const qty = Math.max(0, quantity)

        if (index !== -1) {
            const existing = products[index]
            // removeProduct() never actually removes the row — it just sets
            // Visible=0 and leaves it in the array (SalesOrderV3.js's own
            // design, not something to change here). Re-adding a previously
            // removed line must rebuild it from the CURRENT catalog row, not
            // reuse whatever price/cost/level was left on the old object —
            // otherwise it silently keeps a stale UnitPrice from before the
            // removal (confirmed 2026-08-20: removed a line after manually
            // pricing it to the floor, re-added via the catalog, and the cart
            // kept the old floor price instead of the current catalog price
            // for the customer's actual tier).
            if (existing.Visible === 0) {
                products[index] = buildCartLine(row, qty, Father.state.SalesOrder.Customer.CustomerName)
            } else {
                existing.quantityOrdered = qty
                existing.totalCost = qty * Number(existing.UnitPrice)
                existing.Visible = qty > 0 ? 1 : 0
            }
        } else if (qty > 0) {
            const line = buildCartLine(row, qty, Father.state.SalesOrder.Customer.CustomerName)
            products.unshift(line)
        } else {
            this._inFlightItemCodes.delete(row.itemCode)
            return // nothing to add, quantity is 0 and item isn't in cart yet
        }

        // setState is async — refreshInputs() reads DOM nodes by id for every
        // row, including the one just added/removed, so it MUST run after
        // React has committed the new row count, not right after setState()
        // is called. Doing it inline (no callback) crashes on
        // "Cannot set properties of null" for the not-yet-rendered row.
        Father.setState({ Products: products }, () => {
            this._inFlightItemCodes.delete(row.itemCode)
            Father.calculateTotals()
            Father.refreshInputs()
            this.forceUpdate() // re-render this modal's cards with the new quantity
        })
    }

    render() {
        const { showModal, loading, products, total, page, pageSize, search, productLines, selectedLine, priceLevel, isOffline, syncingOffline, previewItem } = this.state
        const totalPages = Math.max(1, Math.ceil(total / pageSize))

        return (
            <>
                <button hidden={this.props.hidden} disabled={this.props.disabled} type="button" onClick={this.open}
                    className={'btn ' + (this.props.colorButton || 'blueButton') + ' btn-lg'}>
                    <BsJournals /> {this.props.titleButton || 'Open Catalogue'}
                </button>

                {/* TABLEFONTBOOST-2026-10-01 — this.props.Padre is SalesOrderV3.js;
                    its own page-level zoom never reaches here on its own
                    because ModalOrders portals to document.body (see that
                    file's own comment) — bodyZoom applies it directly on
                    the modal's actual DOM node instead. */}
                <ModalOrders title="Catalogue" show={showModal} close={this.close} bodyZoom={this.props.Padre.state.tableFontScale}>
                    {/* General indicator (2026-08-19) — so the salesperson always knows
                        which price tier is being applied for this customer before adding
                        anything. Level 1 is the default for a customer with no tier set. */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '8px' }}>
                        {/* OFFLINE-2026-09-29 — visible while the background offline-cache
                            check/save is actually running (see open()). Usually flashes
                            briefly (cache was already fresh, nothing to fetch); stays up
                            longer on a real fetch. Client explicitly asked for a
                            "working" state, not just a "done" toast at the end. */}
                        {syncingOffline && (
                            <span className="cat-pill" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                background: '#F1F5F9', color: '#334155', border: '1px solid #E2E8F0',
                                borderRadius: '999px', padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700,
                            }}>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '0.75rem', height: '0.75rem' }}></span>
                                Syncing for offline use...
                            </span>
                        )}
                        {/* OFFLINE-2026-09-28 — only shown while actually serving the
                            cached copy, so it's never ambiguous whether prices are live. */}
                        {isOffline && (
                            <span className="cat-pill" style={{
                                background: 'rgba(245,158,11,0.1)', color: '#92400E', border: '1px solid #FDE68A',
                                borderRadius: '999px', padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700,
                            }}>Offline — showing saved catalog</span>
                        )}
                        <span className="cat-pill" style={{
                            background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE',
                            borderRadius: '999px', padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700,
                        }}>Pricing: Level {priceLevel}</span>
                    </div>
                    <div className="card mb-3 cat-search">
                        <div className="card-body" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div style={{ flex: 1, minWidth: '220px' }}>
                                <div style={{ position: 'relative' }}>
                                    <AiOutlineSearch style={{ position: 'absolute', left: '10px', top: '10px', color: '#94A3B8' }} />
                                    <input type="text" className="form-control" style={{ paddingLeft: '32px' }}
                                        placeholder="Search item code or description..." value={search}
                                        onChange={this.onSearchChange}
                                        onKeyDown={(e) => { if (e.key === 'Enter') this.searchNow() }} />
                                </div>
                            </div>
                            <div>
                                <select className="form-select" value={selectedLine}
                                    onChange={(e) => this.setState({ selectedLine: e.target.value, page: 1 }, () => this.fetchCatalog())}>
                                    <option value="">All product lines</option>
                                    {productLines.map((pl) => (
                                        <option key={pl.ProductLine} value={pl.ProductLine}>{pl.ProductLineName}</option>
                                    ))}
                                </select>
                            </div>
                            <button className="btn blueButton btn-lg" onClick={this.searchNow}>Search</button>
                        </div>
                    </div>

                    {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>Loading...</div>}

                    <div className="row g-2">
                        {!loading && products.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>No products found</div>
                        )}
                        {products.map((row) => {
                            const qty = this.cartQuantity(row.itemCode)
                            const hasPrice = row.standardPrice !== null && row.standardPrice !== undefined
                            // NOSAGECOST-2026-10-15 — Sage cost is 0 for this product: visible but can't be added
                            // (a zero-cost line later blocks the invoice). Flag comes from the backend; to revert,
                            // delete these 2 lines and use hasPrice again in the 3 spots below.
                            const noSageCost = row.noSageCost === 1 || row.noSageCost === '1' || row.noSageCost === true
                            const canAdd = hasPrice && !noSageCost
                            return (
                                <div key={row.itemCode} className="col-sm-12 col-md-6 col-lg-4 cat-col">
                                    <div className="cat-card" style={{
                                        background: '#ffffff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px',
                                        marginBottom: '10px', display: 'flex', gap: '12px', alignItems: 'center',
                                    }}>
                                        {/* IMGPREVIEW-2026-10-07 — click/tap enlarges the image (see the
                                            preview Modal at the end of render). Only real images open it;
                                            the "not available" placeholder stays static. */}
                                        {row.itemURLImage ? (
                                            <img className="cat-img" src={row.itemURLImage} alt={row.itemCode}
                                                role="button" tabIndex={0} aria-label={`Enlarge image of ${row.itemCode}`}
                                                onClick={() => this.openPreview(row)}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.openPreview(row) } }}
                                                style={{ width: '84px', height: '84px', objectFit: 'contain', borderRadius: '8px', background: '#F8FAFC', flexShrink: 0, border: '1px solid #F1F5F9', padding: '4px', cursor: 'zoom-in' }} />
                                        ) : (
                                            <img className="cat-img" src="/assets/image-not-available.png" alt={row.itemCode}
                                                style={{ width: '84px', height: '84px', objectFit: 'contain', borderRadius: '8px', background: '#F8FAFC', flexShrink: 0, border: '1px solid #F1F5F9', padding: '4px' }} />
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                                <span className="cat-badge" style={{ background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>{row.itemCode}</span>
                                                <span className="cat-badge" style={{ background: 'rgba(16,185,129,0.08)', color: '#065F46', border: '1px solid #A7F3D0', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>On hand: {row.quantity || 0}</span>
                                            </div>
                                            {/* Long description (2026-08-20, client's ask) — from CI_Item, falls back to the short one only when it's not synced yet.
                                                TABLEFONTBOOST-2026-10-01 — fontSize matches the SAME base as .textTableSize
                                                (css/table-font-boost.css) exactly, client's ask: "todo deberia estar en el mismo
                                                tamaño para que el cambio de fuente tenga sentido". Can't use that CSS class here —
                                                this modal portals to document.body (outside .sales-order-v3-page's DOM subtree), so
                                                the scoped selector can't reach it; same literal value instead. */}
                                            <p className="cat-desc" style={{ fontSize: 'calc(21px + 2pt)', margin: '0 0 6px', color: '#0F172A' }}>{row.longDescription || row.description}</p>
                                            {hasPrice ? (
                                                <p className="cat-price" style={{ fontSize: 'calc(21px + 2pt)', fontWeight: 700, color: '#0F172A', margin: '0 0 8px' }}>${NumberFormat(pricesFormat(row.standardPrice))}</p>
                                            ) : (
                                                <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#991B1B', display: 'flex', alignItems: 'center', gap: '4px', margin: '0 0 8px' }}>
                                                    <AiOutlineWarning /> No standard price set
                                                </p>
                                            )}
                                            {noSageCost && (
                                                <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#92400E', display: 'flex', alignItems: 'center', gap: '4px', margin: '0 0 8px' }}>
                                                    <AiOutlineWarning /> No cost in Sage. Ask an administrator.
                                                </p>
                                            )}
                                            <div className="input-group input-group-sm cat-qty" style={{ maxWidth: '160px' }}>
                                                <button className="btn grayButton" type="button" onClick={() => this.setQuantity(row, qty - 1)}>-</button>
                                                <input type="number" min="0" className="form-control text-center" value={qty} disabled={!canAdd}
                                                    onChange={(e) => this.setQuantity(row, Number(e.target.value))} />
                                                {/* No price configured for this company → can't be added. Forces the
                                                    fix at the source (Price Management) instead of an unpriced sale. */}
                                                <button className="btn blueButton" type="button" disabled={!canAdd} title={!hasPrice ? 'Configure a standard price in Price Management first' : (noSageCost ? 'No cost in Sage. Ask an administrator.' : '')}
                                                    onClick={() => this.setQuantity(row, qty + 1)}>+</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                        <div className="btn-group btn-group-lg">
                            <button type="button" disabled={page <= 1} onClick={() => this.setState({ page: page - 1 }, () => this.fetchCatalog())} className="btn blueButton">Prev</button>
                            <span style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: '#64748B' }}>Page {page} of {totalPages}</span>
                            <button type="button" disabled={page >= totalPages} onClick={() => this.setState({ page: page + 1 }, () => this.fetchCatalog())} className="btn blueButton">Next</button>
                        </div>
                    </div>
                </ModalOrders>

                {/* IMGPREVIEW-2026-10-07 — enlarged product image. A second
                    react-bootstrap Modal stacked on top of the Catalogue one:
                    it already closes on a click/tap outside the image (backdrop)
                    and on Escape, and only the top modal reacts to Escape/focus,
                    so the Catalogue underneath stays open. The z-index bump
                    lives in general-style.css (.catalog-image-modal) — Bootstrap
                    gives every modal/backdrop the same z-index otherwise. */}
                <Modal show={!!previewItem} onHide={this.closePreview} centered size="lg"
                    className="catalog-image-modal" backdropClassName="catalog-image-backdrop"
                    aria-label={previewItem ? `Image of ${previewItem.itemCode}` : 'Product image'}>
                    {previewItem && (
                        <div style={{ position: 'relative', padding: '1.25rem', background: '#FFFFFF', borderRadius: '12px' }}>
                            <button type="button" onClick={this.closePreview} aria-label="Close image"
                                style={{
                                    position: 'absolute', top: '-16px', right: '-16px', width: '44px', height: '44px',
                                    borderRadius: '50%', background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', color: '#FFFFFF',
                                    border: '2px solid #FFFFFF', fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer',
                                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.2)',
                                }}>
                                ✕
                            </button>
                            <img src={previewItem.url} alt={previewItem.itemCode}
                                style={{ display: 'block', width: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: '8px', background: '#F8FAFC' }} />
                            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                                <span style={{ background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '2px 8px', fontSize: '0.8rem', fontWeight: 700 }}>{previewItem.itemCode}</span>
                                <span style={{ color: '#0F172A', fontSize: '0.95rem' }}>{previewItem.description}</span>
                            </div>
                        </div>
                    )}
                </Modal>
            </>
        )
    }
}
